import React, { useState, useEffect } from 'react';
import { adminApi } from '../../services/api';
import { toast } from 'sonner';
import { ToggleLeft, ToggleRight, Loader2, Lock, Sparkles, AlertCircle, Save, X } from 'lucide-react';


export default function SuperadminFeatureToggles() {
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await adminApi.get('/superadmin/feature-toggles');
      setModules(r.data.modules || []);
    } catch {
      toast.error('Errore caricamento moduli');
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const toggle = async (m, nextEnabled, note = '') => {
    try {
      await adminApi.put('/superadmin/feature-toggles', {
        key: m.key, enabled: nextEnabled, note,
      });
      toast.success(nextEnabled ? `"${m.label}" attivato` : `"${m.label}" disattivato`);
      setModules(prev => prev.map(x => x.key === m.key ? { ...x, enabled: nextEnabled, note } : x));
      setEditingKey(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Errore');
    }
  };

  const saveNote = async (m) => {
    await toggle(m, m.enabled, noteDraft.trim());
  };

  if (loading) {
    return (
      <div className="p-10 flex justify-center">
        <Loader2 className="animate-spin text-[#D44E20]" size={28} />
      </div>
    );
  }

  const groups = Array.from(new Set(modules.map(m => m.group)));
  const activeCount = modules.filter(m => !m.core && m.enabled).length;
  const disabledCount = modules.filter(m => !m.core && !m.enabled).length;

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="feature-toggles-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#1A1A1A] mb-1">Moduli Admin</h1>
        <p className="text-sm text-slate-500">
          Accendi o spegni intere aree della dashboard admin per tutti i brand. I moduli disabilitati scompaiono dalla sidebar.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kpi-active">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Moduli attivi</p>
          <p className="text-2xl font-bold text-emerald-600">{activeCount}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid="kpi-disabled">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Disabilitati</p>
          <p className="text-2xl font-bold text-amber-600">{disabledCount}</p>
        </div>
        <div className="bg-white border border-slate-200 rounded-xl p-4">
          <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Essenziali (sempre ON)</p>
          <p className="text-2xl font-bold text-slate-600">{modules.filter(m => m.core).length}</p>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 mb-6 flex items-start gap-2">
        <AlertCircle size={16} className="text-blue-600 mt-0.5 flex-shrink-0" />
        <p className="text-xs text-blue-900">
          La modifica ha effetto <b>immediato per tutti i brand</b>. Gli admin troveranno un banner che spiega che il modulo è temporaneamente disabilitato.
        </p>
      </div>

      <div className="space-y-6">
        {groups.map(group => {
          const groupMods = modules.filter(m => m.group === group);
          return (
            <div key={group} data-testid={`group-${group.replace(/\s+/g, '-').toLowerCase()}`}>
              <h3 className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">{group}</h3>
              <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
                {groupMods.map(m => (
                  <div key={m.key} className="p-4 flex items-start gap-4" data-testid={`row-${m.key}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold text-[#1A1A1A]">{m.label}</p>
                        {m.core && (
                          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                            <Lock size={10} /> Essenziale
                          </span>
                        )}
                        {!m.core && !m.enabled && (
                          <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                            Disabilitato
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 font-mono">{m.route}</p>
                      {editingKey === m.key ? (
                        <div className="mt-2 flex items-center gap-2">
                          <input
                            value={noteDraft}
                            onChange={e => setNoteDraft(e.target.value)}
                            placeholder="Es. In arrivo a marzo"
                            data-testid={`note-input-${m.key}`}
                            className="flex-1 px-3 py-1.5 border border-slate-200 rounded-lg text-sm"
                          />
                          <button
                            onClick={() => saveNote(m)}
                            data-testid={`save-note-${m.key}`}
                            className="px-3 py-1.5 bg-[#D44E20] text-white rounded-lg text-xs font-semibold hover:shadow-md flex items-center gap-1"
                          >
                            <Save size={12} /> Salva
                          </button>
                          <button
                            onClick={() => setEditingKey(null)}
                            className="w-7 h-7 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ) : m.note ? (
                        <button
                          onClick={() => { setEditingKey(m.key); setNoteDraft(m.note); }}
                          data-testid={`edit-note-${m.key}`}
                          className="mt-1.5 text-xs text-slate-600 italic hover:text-[#D44E20]"
                        >
                          <Sparkles size={10} className="inline mr-1" />
                          {m.note}
                        </button>
                      ) : !m.core && (
                        <button
                          onClick={() => { setEditingKey(m.key); setNoteDraft(''); }}
                          data-testid={`add-note-${m.key}`}
                          className="mt-1.5 text-xs text-slate-400 hover:text-[#D44E20]"
                        >
                          + Aggiungi nota (es. "In arrivo a marzo")
                        </button>
                      )}
                    </div>
                    <button
                      onClick={() => !m.core && toggle(m, !m.enabled, m.note || '')}
                      disabled={m.core}
                      data-testid={`toggle-${m.key}`}
                      className={`flex-shrink-0 transition-colors ${m.core ? 'opacity-30 cursor-not-allowed' : 'hover:scale-105'}`}
                      title={m.core ? 'Modulo essenziale: non disabilitabile' : (m.enabled ? 'Disabilita' : 'Abilita')}
                    >
                      {m.enabled ? (
                        <ToggleRight size={36} className="text-emerald-500" />
                      ) : (
                        <ToggleLeft size={36} className="text-slate-300" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
